import type { Playlist, VideoData } from './api';

const defaultError = {
  title: "Playback Error",
  description: "Unable to play the video. Please try again later.",
};

export const errorTypes: Record<
  "api" | "default" | string,
  {
    title: string;
    description: string;
  }
> = {
  api: {
    title: "Failed to Retrieve Video Info",
    description:
      "Error fetching video details. Check your videoId and internet connection and try again.",
  },
  ERROR_CODE_NOT_AUTHENTICATED: {
    title: "Authorisation",
    description: "You don't have permission to play this video. ",
  },
  ERROR_CODE_GEO_BLOCKED: {
    title: "Geoblocking Restriction",
    description:
      "Sorry, this video is not available in your region due to geoblocking restrictions.",
  },
  ERROR_CODE_NOT_ENTITLED: {
    title: "Not entitled",
    description: "Sorry, you are not entitled to play this video.",
  },
  ERROR_CODE_VPN_BLOCKED: {
    title: "VPN BLOCKED",
    description: "This video is not available due to the use of a VPN.",
  },
  ERROR_CODE_UNSPECIFIED: defaultError,
  ERROR_CODE_SIGNATURE_MISMATCH: defaultError,
  ERROR_CODE_INVALID_DOMAIN: defaultError,
  default: defaultError,
};

export const getMediaTypeFromSource = (
  source: string
): "video/mp4" | "application/vnd.apple.mpegurl" | "video/x-flv" => {
  if (source.includes(".mp4")) {
    return "video/mp4";
  }
  if (source.includes(".m3u8")) {
    return "application/vnd.apple.mpegurl";
  }
  return "video/x-flv"; // Default return
};

export const getSourceObject = ({
  source,
}: {
  source: string;
}): {
  src: string;
  type: string;
  withCredentials: boolean;
} => {
  const mediaType = getMediaTypeFromSource(source);

  return {
    src: source,
    type: mediaType,
    withCredentials: false, // Set to false as per the original comment about CORS issues
  };
};

export const formatTime = (seconds = 0): string => {
  const ss = seconds < 0 ? 0 : seconds;
  let s: number | string = Math.floor(ss % 60);
  let m: number | string = Math.floor((ss / 60) % 60);
  let h: number | string = Math.floor(ss / 3600);

  if (isNaN(ss) || ss === Infinity) {
    s = "-";
    m = "-";
    h = "-";
  }

  h = Number(h) > 0 ? `${h}:` : "";
  m = `${Number(m) < 10 ? `0${m}` : m}:`;
  s = Number(s) < 10 ? `0${s}` : s;

  return h + m + s;
};

export const findHLSPlaylist = (video?: VideoData): Playlist | undefined => {
  return video?.playlists?.find((playlist) => {
    return playlist.format === 'HLS' || 
           playlist.format === 'hls' || 
           playlist.format === 'PLAYLIST_FORMAT_HLS';
  });
};

export const getErrorType = (error?: string | Error, video?: VideoData) => {
  if (video?.error && errorTypes[video.error]) {
    return video.error;
  }
  return 'DEFAULT_ERROR';
};
